import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import {
  getOpportunityById,
  getPipelineStages,
  moveOpportunityStage,
  updateOpportunity,
} from "../services/crmOpportunitiesService";
import {
  createActivity,
  completeActivity,
} from "../services/crmActivitiesService";
import ActivityComposer from "../components/ActivityComposer";
import ActivityTimeline from "../components/ActivityTimeline";
import { supabase } from "../../Services/supabaseClient";
import "../styles/crm.css";

const OpportunityDetailsPage = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { companyId } = useCompany();

  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [message, setMessage] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: "",
    amount: "",
    probability: "",
    expected_close_date: "",
    next_step: "",
    description: "",
  });

  const load = useCallback(async () => {
    if (!companyId || !opportunityId) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const [opportunityResult, stageRows] = await Promise.all([
        getOpportunityById(companyId, opportunityId),
        getPipelineStages(companyId),
      ]);

      setData(opportunityResult);
      setStages(stageRows);
      setForm({
        name: opportunityResult.opportunity.name || "",
        amount: String(opportunityResult.opportunity.amount || ""),
        probability: String(opportunityResult.opportunity.probability || ""),
        expected_close_date:
          opportunityResult.opportunity.expected_close_date || "",
        next_step: opportunityResult.opportunity.next_step || "",
        description: opportunityResult.opportunity.description || "",
      });
    } catch (error) {
      setMessage(error.message || "Failed to load opportunity");
    } finally {
      setLoading(false);
    }
  }, [companyId, opportunityId]);

  useEffect(() => {
    load();
  }, [load]);

  const weightedValue = useMemo(() => {
    const amount = Number(data?.opportunity?.amount || 0);
    const probability = Number(data?.opportunity?.probability || 0);
    return (amount * probability) / 100;
  }, [data]);

  const handleFormChange = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!companyId || !opportunityId || !form.name.trim()) {
      return;
    }

    setBusyAction("save");
    setMessage("");

    try {
      await updateOpportunity(companyId, opportunityId, {
        name: form.name.trim(),
        amount: Number(form.amount || 0),
        probability: Number(form.probability || 0),
        expected_close_date: form.expected_close_date || null,
        next_step: form.next_step.trim() || null,
        description: form.description.trim() || null,
      });

      setEditMode(false);
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to update opportunity");
    } finally {
      setBusyAction("");
    }
  }, [companyId, opportunityId, form, load]);

  const handleStageChange = useCallback(
    async (toStageId) => {
      if (!companyId || !opportunityId || !toStageId) {
        return;
      }

      setBusyAction("stage");
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error("User not found");
        }

        await moveOpportunityStage({
          opportunityId,
          companyId,
          toStageId,
          changedBy: user.id,
          note: "",
        });

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to move stage");
      } finally {
        setBusyAction("");
      }
    },
    [companyId, opportunityId, load]
  );

  const handleCreateActivity = useCallback(
    async (activityPayload) => {
      if (!companyId || !opportunityId) {
        return;
      }

      setBusyAction("activity");
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error("User not found");
        }

        await createActivity({
          company_id: companyId,
          owner_id: user.id,
          related_type: "opportunity",
          related_id: opportunityId,
          activity_type: activityPayload.activity_type,
          subject: activityPayload.subject,
          description: activityPayload.description,
          due_at: activityPayload.due_at,
        });

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to create activity");
      } finally {
        setBusyAction("");
      }
    },
    [companyId, opportunityId, load]
  );

  const handleCompleteActivity = useCallback(
    async (activityId) => {
      if (!companyId || !activityId) {
        return;
      }

      setBusyAction(activityId);
      setMessage("");

      try {
        await completeActivity(activityId, companyId);
        await load();
      } catch (error) {
        setMessage(error.message || "Failed to complete activity");
      } finally {
        setBusyAction("");
      }
    },
    [companyId, load]
  );

  const handleCreateQuote = useCallback(() => {
    if (!data?.opportunity?.id) {
      return;
    }

    navigate(`/crm/quotes?opportunityId=${data.opportunity.id}`);
  }, [navigate, data]);

  if (loading) {
    return <div className="crm-message">Loading opportunity...</div>;
  }

  if (!data) {
    return (
      <div className="crm-message">{message || "No opportunity found."}</div>
    );
  }

  return (
    <div>
      <h2 className="crm-page-title">Opportunity Details</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-list-title">{data.opportunity.name}</h3>
        <div className="crm-list-subtext">
          Account: {data.opportunity.crm_accounts?.name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Contact: {data.opportunity.crm_contacts?.full_name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Stage: {data.opportunity.crm_pipeline_stages?.name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Status: {data.opportunity.status || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Amount: ₦{Number(data.opportunity.amount || 0).toLocaleString()}
        </div>
        <div className="crm-list-subtext">
          Probability:{" "}
          {Number(data.opportunity.probability || 0).toLocaleString()}%
        </div>
        <div className="crm-list-subtext">
          Weighted Value: ₦{Number(weightedValue || 0).toLocaleString()}
        </div>

        <div className="crm-inline-actions" style={{ marginTop: "12px" }}>
          <button
            className="crm-small-btn alt"
            onClick={() => setEditMode(true)}
          >
            Edit Opportunity
          </button>

          <button className="crm-primary-btn" onClick={handleCreateQuote}>
            Create Quote
          </button>
        </div>
      </div>

      {editMode ? (
        <div className="crm-table-card" style={{ marginBottom: "20px" }}>
          <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
            Edit Opportunity
          </h3>

          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Name</label>
              <input
                className="crm-input"
                value={form.name}
                onChange={(event) =>
                  handleFormChange("name", event.target.value)
                }
              />
            </div>

            <div className="crm-form-group">
              <label>Amount</label>
              <input
                className="crm-input"
                type="number"
                value={form.amount}
                onChange={(event) =>
                  handleFormChange("amount", event.target.value)
                }
              />
            </div>
          </div>

          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Probability</label>
              <input
                className="crm-input"
                type="number"
                value={form.probability}
                onChange={(event) =>
                  handleFormChange("probability", event.target.value)
                }
              />
            </div>

            <div className="crm-form-group">
              <label>Expected Close Date</label>
              <input
                className="crm-input"
                type="date"
                value={form.expected_close_date}
                onChange={(event) =>
                  handleFormChange("expected_close_date", event.target.value)
                }
              />
            </div>
          </div>

          <div className="crm-form-group">
            <label>Next Step</label>
            <input
              className="crm-input"
              value={form.next_step}
              onChange={(event) =>
                handleFormChange("next_step", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Description</label>
            <textarea
              className="crm-textarea"
              rows={4}
              value={form.description}
              onChange={(event) =>
                handleFormChange("description", event.target.value)
              }
            />
          </div>

          <div className="crm-inline-actions">
            <button
              className="crm-primary-btn"
              onClick={handleSave}
              disabled={busyAction === "save"}
            >
              {busyAction === "save" ? "Saving..." : "Save Changes"}
            </button>

            <button
              className="crm-secondary-btn"
              onClick={() => setEditMode(false)}
              disabled={busyAction === "save"}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Change Stage
        </h3>

        <div className="crm-form-group">
          <label>Stage</label>
          <select
            className="crm-select"
            value={data.opportunity.stage_id || ""}
            onChange={(event) => handleStageChange(event.target.value)}
            disabled={busyAction === "stage"}
          >
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Quotes
        </h3>

        {data.quotes.length === 0 ? (
          <div className="crm-message">No quotes linked yet.</div>
        ) : (
          data.quotes.map((quote) => (
            <div key={quote.id} className="crm-list-card">
              <h3 className="crm-list-title">
                {quote.quote_number || quote.id}
              </h3>
              <div className="crm-list-subtext">
                Status: {quote.status || "N/A"}
              </div>
              <div className="crm-list-subtext">
                Total: ₦{Number(quote.total || 0).toLocaleString()}
              </div>
              <div className="crm-inline-actions">
                <Link className="crm-small-btn" to={`/crm/quotes/${quote.id}`}>
                  Open Quote
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      <ActivityComposer
        onCreate={handleCreateActivity}
        busy={busyAction === "activity"}
      />

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Activity Timeline
        </h3>

        {data.activities.length === 0 ? (
          <div className="crm-message">No activities yet.</div>
        ) : (
          <ActivityTimeline
            activities={data.activities}
            onComplete={handleCompleteActivity}
            completingId={busyAction}
          />
        )}
      </div>

      <div className="crm-table-card">
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Stage History
        </h3>

        {data.stageHistory.length === 0 ? (
          <div className="crm-message">No stage history yet.</div>
        ) : (
          data.stageHistory.map((row) => (
            <div key={row.id} className="crm-list-card">
              <h3 className="crm-list-title">
                {row.from_stage?.name || "Unknown"} →{" "}
                {row.to_stage?.name || "Unknown"}
              </h3>
              <div className="crm-list-subtext">
                Changed At:{" "}
                {row.changed_at
                  ? new Date(row.changed_at).toLocaleString()
                  : "N/A"}
              </div>
              <div className="crm-list-subtext">Note: {row.note || "N/A"}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OpportunityDetailsPage;
